import type { AsyncDuckDB, DuckDBConfig, DuckDBBundle, Logger, LogLevel, DuckDBDataProtocol } from '@duckdb/duckdb-wasm';
import { Engine, EngineFeatures, TableRef, RegisterTableParams, TableSourceType, TableRegistrationOptions, TableChangeCallback, ExportToURLOptions } from '../core/engine';
import { MaterializedTableData } from '../core/table-data';
import { TableView, BaseTableView, ViewSource } from '../core/table-view';
import { Query, TableSchema, MaterializeOptions, TableFormat, ColumnSchema, TableDataSource } from '../core/types';
import { DuckDBWasmSQLBuilder } from './sql-builder';
import { DataType, Float64, Int64, Utf8, Bool, DateDay, TimestampMillisecond } from 'apache-arrow';
import { DataTableCache } from '../core/data-table-cache';
import { 
  mapDuckDBTypeToColumnType, 
  generateTableId, 
  getDefaultPath, 
  cleanFileName, 
  getTableFormatFromExtension,
  validateTableSchema 
} from './utils';
import { 
  initializeDuckDB, 
  getDuckDB, 
  getBundle,
  DuckDBEngineConfig,
  DuckDBModule 
} from './initialization';
import {
  registerFileInDuckDB,
  registerTableWithDuckDB,
  inferSchemaFromURL,
  inferSchemaFromLocalTable,
  type DuckDBConnection
} from './table-registration';

/**
 * Information about a registered table
 */
interface RegisteredTable {
  schema: TableSchema;
  path: string;
  source: TableDataSource;
}

/**
 * DuckDB WebAssembly engine implementation
 * 
 * This class provides a factory method for creating initialized instances.
 * 
 * @memoryUsage This engine maintains significant memory structures:
 * - DuckDB WASM instance has its own memory pool
 * - Maintains internal buffers and caches
 * - Memory usage increases with:
 *   - Number of registered tables
 *   - Size of query results
 *   - Concurrent query execution
 * - Always call dispose() when done to free resources
 */
export class DuckDBEngine implements Engine {
  private db: AsyncDuckDB | null = null;
  private isInitialized = false;
  private readonly tables = new Map<string, RegisteredTable>();
  private readonly cache = new DataTableCache();
  
  /**
   * Callbacks to be called when tables change
   * @private
   */
  private tableChangeCallbacks: Set<TableChangeCallback> = new Set();

  /**
   * Create a new DuckDB engine instance
   * @private This constructor is private - use DuckDBEngine.create() instead
   */
  private constructor() {
    // fileStore initialization removed
  }

  /**
   * Create and initialize a new DuckDBEngine instance
   * @returns A promise that resolves to an initialized DuckDBEngine instance
   */
  public static async create(): Promise<DuckDBEngine> {
    const engine = new DuckDBEngine();
    await engine.initialize();
    return engine;
  }

  get features(): EngineFeatures {
    return {
      supportsSQL: true
    };
  }

  /**
   * Initialize the DuckDB instance
   * @returns Promise that resolves when initialization is complete
   */
  public async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Use the new initialization function
      this.db = await initializeDuckDB();
      this.isInitialized = true;
    } catch (error) {
      console.error('Error during DuckDB initialization:', error);
      this.db = null;
      throw new Error(`Failed to initialize DuckDB: ${error}`);
    }
  }

  /**
   * Ensures the engine is initialized before performing operations
   * @private
   */
  private async ensureInitialized(): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }
  }

  async registerTable(params: RegisterTableParams): Promise<TableRef> {
    await this.ensureInitialized();
    
    const { source, options = {} } = params;
    
    // Generate or use provided path
    const id = options.path || generateTableId({ source });
    const path = options.path || getDefaultPath({ source, id });
    
    // Check if a table with this ID already exists
    if (this.tables.has(id)) {
      throw new Error(`Table already exists: ${id}`);
    }
    
    if (options.validateSchema) {
      const existingSchema = this.getSchemaFromSource(source);
      if (existingSchema) {
        validateTableSchema({ schema: existingSchema });
      }
    }
    
    let schema: TableSchema;
    
    if (this.db) {
      const conn = await this.db.connect();
      try {
        // 1. CREATE TABLE/VIEW FIRST (files are now downloaded and cached locally)
        await registerTableWithDuckDB({ 
          conn, 
          db: this.db, 
          cache: this.cache, 
          id, 
          source 
        });
        
        // 2. INTROSPECT SCHEMA FROM LOCAL TABLE (no additional HTTP requests)
        schema = await inferSchemaFromLocalTable({ conn, tablePath: path });
        
        // 3. No need to verify queryability - files are guaranteed to be local and available
        
      } finally {
        conn.close();
      }
    } else {
      // Fallback for when DB is not available
      schema = this.getSchemaFromSource(source);
    }
    
    // Store the table information
    this.tables.set(id, {
      schema,
      path,
      source
    });
    
    // Create a table reference
    const tableRef: TableRef = {
      id,
      path,
      schema,
      source
    };
    
    // Only notify callbacks after the table is fully ready
    this.notifyTableChange();
    
    return tableRef;
  }

  async getTableView(ref: TableRef): Promise<TableView> {
    if (!this.isInitialized || !this.db) {
      throw new Error('DuckDB not initialized');
    }

    const tableInfo = this.tables.get(ref.id);
    if (!tableInfo) {
      throw new Error(`Table not found: ${ref.id}`);
    }

    return new BaseTableView(ref.schema, { type: 'table', tableRef: ref });
  }

  async transform(view: TableView, query: Query): Promise<TableView> {
    if (!this.isInitialized || !this.db) {
      throw new Error('DuckDB not initialized');
    }

    return new BaseTableView(view.schema, {
      type: 'query',
      tableRef: view.source.tableRef,
      query
    });
  }

  async unregisterTable(ref: TableRef): Promise<void> {
    await this.ensureInitialized();
    
    // Check if the table exists
    if (!this.tables.has(ref.id)) {
      throw new Error(`Table not found: ${ref.id}`);
    }
    
    // Get the table information
    const table = this.tables.get(ref.id)!;
    
    // Remove the table from DuckDB
    if (this.db) {
      const conn = await this.db.connect();
      try {
        // Determine if this is a table or view based on the source type
        const isView = table.source.type === TableSourceType.LOCAL_FILE || 
                       table.source.type === TableSourceType.URL ||
                       table.source.type === TableSourceType.DATATABLE;
        
        if (isView) {
          // For LOCAL_FILE, URL, and DATATABLE sources, we create views
          await conn.query(`DROP VIEW IF EXISTS "${ref.path}"`);
        } else {
          // For MATERIALIZED sources, we create tables
          await conn.query(`DROP TABLE IF EXISTS "${ref.path}"`);
        }
      } catch (error) {
        console.error(`Error dropping table/view ${ref.path}:`, error);
      } finally {
        conn.close();
      }
    }
    
    // Remove the table from the registry
    this.tables.delete(ref.id);
    
    // Clean up cache if this was a materialized datatable
    if (table.source.type === TableSourceType.DATATABLE && table.source.materialized) {
      try {
        await this.cache.delete(table.source.datatableId);
      } catch (error) {
        console.warn(`Failed to clean up cache for table ${ref.id}:`, error);
      }
    }
    
    // Notify callbacks that tables have changed
    this.notifyTableChange();
  }

  async materialize(view: TableView, options?: MaterializeOptions): Promise<MaterializedTableData> {
    if (!this.isInitialized || !this.db) {
      throw new Error('DuckDB not initialized');
    }

    const conn = await this.db.connect();
    try {
      const { source } = view;
      let sql: string;

      switch (source.type) {
        case 'table':
          sql = `SELECT * FROM "${source.tableRef.path}"`;
          break;
        case 'query':
          sql = DuckDBWasmSQLBuilder.buildQuery({
            tableName: source.tableRef.path,
            query: source.query
          });
          break;
      }

      // Remove trailing semicolons before adding LIMIT
      sql = sql.trim().replace(/;+$/, '');

      // Apply paging with default limit of 1000 rows, but only if the SQL doesn't already have a LIMIT clause
      const { size = 1000, number = 0 } = options?.page || {};
      const offset = size * number;
      
      // Check if the SQL already has a LIMIT clause
      if (!sql.toUpperCase().includes(' LIMIT ')) {
        sql += ` LIMIT ${size} OFFSET ${offset}`;
      } else if (!sql.toUpperCase().includes(' OFFSET ')) {
        // If there's already a LIMIT but no OFFSET, add just the OFFSET
        sql += ` OFFSET ${offset}`;
      }

      const result = await conn.query(sql);
      return new MaterializedTableData(result);
    } finally {
      await conn.close();
    }
  }

  /**
   * Export query results to a URL without pagination
   * @param options Export options including view and signedUrl
   * @returns Promise that resolves when the export is complete
   */
  async exportToURL(options: ExportToURLOptions): Promise<void> {
    if (!this.isInitialized || !this.db) {
      throw new Error('DuckDB not initialized');
    }

    const { view, signedUrl } = options;
    
    // Validate inputs
    if (!view) {
      throw new Error('View is required for export');
    }
    
    if (!signedUrl) {
      throw new Error('Signed URL is required for export');
    }
    
    // Validate URL format (basic check)
    try {
      new URL(signedUrl);
    } catch (error) {
      throw new Error(`Invalid signed URL: ${signedUrl}`);
    }
    
    const conn = await this.db.connect();
    
    try {
      const source: ViewSource = view.source;
      let sql: string;

      // Build the SQL query without pagination limits
      if (source.type === 'table') {
        sql = `SELECT * FROM "${source.tableRef.path}"`;
      } else if (source.type === 'query') {
        sql = DuckDBWasmSQLBuilder.buildQuery({
          tableName: source.tableRef.path,
          query: source.query
        });
      } else {
        // This should never happen with proper ViewSource types
        throw new Error('Unsupported source type');
      }

      // Remove trailing semicolons
      sql = sql.trim().replace(/;+$/, '');

      // Create a temporary file name for the Parquet export
      const tempFileName = `export_${Date.now()}.parquet`;
      
      // Use DuckDB's COPY statement to export to a Parquet file
      await conn.query(`COPY (${sql}) TO '${tempFileName}' (FORMAT PARQUET)`);
      
      // Get the Parquet data from the temporary file
      const parquetBuffer = await this.db!.copyFileToBuffer(tempFileName);
      
      if (!parquetBuffer || parquetBuffer.byteLength === 0) {
        throw new Error('Failed to export data to Parquet format');
      }
      
      // Upload the Parquet data to the signed URL
      const uploadResponse = await fetch(signedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/octet-stream',
        },
        body: parquetBuffer,
      });
      
      if (!uploadResponse.ok) {
        throw new Error(`Failed to upload data: ${uploadResponse.status} ${uploadResponse.statusText}`);
      }
      
      // Clean up the temporary file
      try {
        await this.db!.dropFile(tempFileName);
      } catch (error) {
        console.warn(`Failed to delete temporary file ${tempFileName}:`, error);
      }
    } catch (error) {
      console.error('Export failed:', error);
      throw new Error(`Failed to export data: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      await conn.close();
    }
  }

  async dispose(): Promise<void> {
    // Close the database connection
    if (this.db) {
      try {
        // The correct method is terminate(), not close()
        await this.db.terminate();
      } catch (error) {
        console.error('Error closing DuckDB:', error);
      }
      this.db = null;
    }
    
    // Close the cache
    try {
      await this.cache.close();
    } catch (error) {
      console.error('Error closing cache:', error);
    }
    
    this.isInitialized = false;
    this.tables.clear();
  }

  private getSchemaFromSource(source: TableDataSource): TableSchema {
    switch (source.type) {
      case TableSourceType.LOCAL_FILE:
        return source.schema;
      case TableSourceType.URL:
      case TableSourceType.DATATABLE:
        if (!source.schema) {
          throw new Error('Schema is required for URL/DATATABLE source. Use an engine implementation that supports schema inference.');
        }
        return source.schema;
      case TableSourceType.MATERIALIZED:
        return source.data.schema;
    }
  }

  /**
   * List all registered tables
   * @returns Array of TableRef objects for all registered tables
   */
  async listRegisteredTables(): Promise<TableRef[]> {
    if (!this.isInitialized) {
      await this.ensureInitialized();
    }

    const result: TableRef[] = [];
    
    // Convert the internal tables map to TableRef objects
    for (const [id, tableInfo] of Array.from(this.tables.entries())) {
      const tableRef: TableRef = {
        id,
        path: tableInfo.path,
        schema: tableInfo.schema,
        source: tableInfo.source  // Use the stored source instead of creating a placeholder
      };
      
      result.push(tableRef);
    }
    
    return result;
  }

  /**
   * Notify all registered callbacks that tables have changed
   * @private
   */
  private notifyTableChange(): void {
    // Call all registered callbacks
    this.tableChangeCallbacks.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('Error in table change callback:', error);
      }
    });
  }
  
  /**
   * Register a callback to be called when tables change
   * @param callback The callback to register
   * @returns A function to unregister the callback
   */
  onTablesChange(callback: TableChangeCallback): () => void {
    this.tableChangeCallbacks.add(callback);
    
    // Return a function to unregister the callback
    return () => {
      this.tableChangeCallbacks.delete(callback);
    };
  }

  /**
   * Register a table from a URL
   * This is a convenience method for loading data from a URL
   * @param params Configuration object with URL registration parameters
   * @param params.url The URL to load data from
   * @param params.format The format of the data (CSV, JSON, etc.)
   * @param params.options Optional registration options
   * @returns Table reference
   */
  async registerFromURL(params: { 
    url: string; 
    format: TableFormat; 
    options?: TableRegistrationOptions;
  }): Promise<TableRef> {
    await this.ensureInitialized();
    
    const { url, format, options } = params;
    
    // Let the registerTable method handle schema inference
    return this.registerTable({
      source: {
        type: TableSourceType.URL,
        url,
        format,
        schema: { columns: [] } // Empty schema that will be replaced by inference
      } as const,
      options
    });
  }

  /**
   * Register a dataset table from a URL with explicit datatable ID for cache operations
   * This method is specifically for dataset tables that need to be cached with their database ID
   * @param params Configuration object with dataset table registration parameters
   * @param params.url The URL to load data from
   * @param params.format The format of the data (CSV, JSON, etc.)
   * @param params.options Optional registration options
   * @param params.materialized Whether to materialize the table in cache
   * @param params.datatableId The datatable ID from the database for cache operations
   * @returns Table reference
   */
  async registerDatasetTable(params: { 
    url: string; 
    format: TableFormat; 
    options?: TableRegistrationOptions;
    materialized?: boolean;
    datatableId: string;
  }): Promise<TableRef> {
    await this.ensureInitialized();
    
    const { url, format, options, materialized = false, datatableId } = params;
    
    // Let the registerTable method handle schema inference
    return this.registerTable({
      source: {
        type: TableSourceType.DATATABLE,
        url,
        format,
        schema: { columns: [] }, // Empty schema that will be replaced by inference
        materialized, // Pass through the materialized flag
        datatableId // Pass through the datatable ID for cache operations
      } as const,
      options
    });
  }

  /**
   * Register a table from a File object
   * This method uses DuckDB's direct file handling capabilities
   * @param file File object to register
   * @param options Optional registration options
   * @returns Table reference
   */
  async registerFromFile(file: File, options?: TableRegistrationOptions): Promise<TableRef> {
    await this.ensureInitialized();
    
    if (!this.db) {
      throw new Error('DuckDB not initialized');
    }
    
    try {
      // Clean the file name to match how it will be registered in DuckDB
      const cleanedFileName = cleanFileName({ filename: file.name });
      
      // Check if a table with this name already exists in our registry
      const existingTableId = Array.from(this.tables.keys()).find(id => {
        const tableInfo = this.tables.get(id);
        return tableInfo && tableInfo.path === cleanedFileName;
      });
      
      // If we found an existing table with the same path and upsert is enabled, unregister it first
      if (existingTableId && options?.upsert) {
        const existingTable = { id: existingTableId, path: cleanedFileName } as TableRef;
        await this.unregisterTable(existingTable);
      }
      
      // Register the file with DuckDB
      const tableRef = await registerFileInDuckDB({
        file,
        db: this.db,
        options
      });
      
      // Store the table information
      this.tables.set(tableRef.id, {
        schema: tableRef.schema,
        path: tableRef.path,
        source: tableRef.source
      });
      
      // No need to verify queryability - local files are guaranteed to be available
      
      // Only notify callbacks after the table is fully ready
      this.notifyTableChange();
      
      return tableRef;
    } catch (error) {
      console.error(`DuckDBEngine: Error registering file ${file.name}:`, error);
      throw new Error(`Failed to register file: ${error instanceof Error ? error.message : String(error)}`);
    }
  }


} 